import {useQuery} from '@tanstack/react-query'
import {UserCircle2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {CardSection} from '@/components/shared/CardSection'
import {EntityCardLink} from '@/components/shared/EntityCardLink'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Skeleton} from '@/components/ui/skeleton'
import {Stack} from '@/components/shared/Stack'
import {labelOf} from '@/hooks/app-config-helpers'
import {useAppConfig} from '@/hooks/use-app-config'
import type {IdentityRelationship} from '@/types/identity'

interface IdentityManagerSectionProps {
    identityId: string
}

const QUERY_KEY = ['identity-manager']

/**
 * "Manager" / "Responsible Persons" card on the IdentityDetail page (epic 986
 * Phase D, spec §7).
 *
 * <p>Renders the Identity's active outgoing IdentityRelationship rows whose
 * type carries {@code responsibilities ∋ APPROVAL} (today this is the
 * MANAGED_BY edge). The label flips between "Manager" (single row) and
 * "Responsible Persons" (multiple rows) — multi-employment safe because the
 * displayed name is {@code toIdentity.displayName}, never falling back to
 * {@code toIdentity.party.displayName}.
 *
 * <p>Three independent states:
 * <ul>
 *   <li>Loading — skeleton placeholders inside the card body</li>
 *   <li>Error — {@code <ErrorBanner>} with retry; the rest of the page keeps
 *       rendering</li>
 *   <li>Empty — {@code <InlineEmptyText>} "No responsible persons assigned"</li>
 * </ul>
 */
export function IdentityManagerSection({identityId}: IdentityManagerSectionProps) {
    const i18n = useI18n()
    const services = useServices()
    const {identityTypes} = useAppConfig()

    const query = useQuery({
        queryKey: [...QUERY_KEY, identityId],
        queryFn: () => services.identityRelationships.fromIdentity(identityId),
        enabled: !!identityId,
    })

    const relationships = (query.data ?? []).filter((r) =>
        Array.isArray(r.responsibilities) && r.responsibilities.includes('APPROVAL'))
    // WHY: i18next picks `_other` for count=0 by default — clamp to 1 so the
    // empty section title stays "Manager" (the empty-state body still says
    // "No manager assigned"). count=1 → singular, count≥2 → "_other" plural.
    const titleCount = Math.max(1, relationships.length)

    return (
        <CardSection
            icon={UserCircle2}
            title={i18n.identities.managerSection({count: titleCount})}
            description={i18n.identities.managerSectionDescription({count: titleCount})}
            data-testid="section-manager"
        >
            {query.isLoading && (
                <Stack gap="sm" data-testid="manager-section-loading">
                    <Skeleton className="h-12 w-full"/>
                </Stack>
            )}
            {query.isError && !query.isLoading && (
                <div data-testid="manager-section-error">
                    <ErrorBanner
                        error={query.error as Error}
                        onRetry={() => query.refetch()}
                    />
                </div>
            )}
            {!query.isLoading && !query.isError && relationships.length === 0 && (
                <InlineEmptyText data-testid="manager-section-empty">
                    {i18n.identities.managerEmptyState}
                </InlineEmptyText>
            )}
            {!query.isLoading && !query.isError && relationships.length > 0 && (
                <Stack gap="sm">
                    {relationships.map((rel: IdentityRelationship) => (
                        <EntityCardLink
                            key={rel.id}
                            to={routes.identityManagement.identities.detail(rel.toIdentity.id)}
                            icon={UserCircle2}
                            subtitle={
                                <>
                                    <span>{i18n.identities.viewIdentityDetails}</span>
                                    {/* eslint-disable-next-line i18next/no-literal-string -- bullet separator between two i18n-driven strings */}
                                    <span aria-hidden="true">{' · '}</span>
                                    <span data-testid={`manager-identity-type-${rel.toIdentity.id}`}>
                                        {labelOf(identityTypes, rel.toIdentity.identityType)}
                                    </span>
                                </>
                            }
                            data-testid={`link-manager-${rel.toIdentity.id}`}
                        >
                            <span data-testid={`manager-display-name-${rel.toIdentity.id}`}>{rel.toIdentity.displayName}</span>
                        </EntityCardLink>
                    ))}
                </Stack>
            )}
        </CardSection>
    )
}
